import * as DialogPrimitive from '@radix-ui/react-dialog'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'
import { dialogOverlayClassName } from './dialog.tsx'
import { glassSurfaceClassName } from './glass.ts'

/**
 * Sheet — a side panel on the same Radix base and conventions as `Dialog`
 * (see `dialog.tsx`): a glass panel with the large brand shadow over the
 * `dialogOverlayClassName` overlay, rendered in place. It slides in from the
 * right (`animate-slide-left`) and fades out. Radix warns without a
 * `SheetTitle`; give it `sr-only` where the design has no visible heading.
 */
function Sheet(props: React.ComponentProps<typeof DialogPrimitive.Root>) {
	return <DialogPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger(
	props: React.ComponentProps<typeof DialogPrimitive.Trigger>,
) {
	return <DialogPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose(props: React.ComponentProps<typeof DialogPrimitive.Close>) {
	return <DialogPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetOverlay({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
	return (
		<DialogPrimitive.Overlay
			data-slot="sheet-overlay"
			className={cn(dialogOverlayClassName, className)}
			{...props}
		/>
	)
}

function SheetContent({
	className,
	side = 'right',
	...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
	/** The edge the panel is attached to; only `right` exists so far. */
	side?: 'right'
}) {
	return (
		<DialogPrimitive.Content
			data-slot="sheet-content"
			data-side={side}
			className={cn(
				glassSurfaceClassName,
				'text-popover-foreground shadow-brand-lg data-[state=open]:animate-slide-left data-[state=closed]:animate-out data-[state=closed]:fade-out-0 fixed inset-y-0 right-0 z-50 flex h-full w-[280px] max-w-[80vw] flex-col gap-4 border-l p-6',
				className,
			)}
			{...props}
		/>
	)
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="sheet-header"
			className={cn('flex flex-col gap-1', className)}
			{...props}
		/>
	)
}

function SheetTitle({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
	return (
		<DialogPrimitive.Title
			data-slot="sheet-title"
			className={cn('text-h6 leading-none font-semibold', className)}
			{...props}
		/>
	)
}

export {
	Sheet,
	SheetTrigger,
	SheetClose,
	SheetOverlay,
	SheetContent,
	SheetHeader,
	SheetTitle,
}
